"use client"
import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ArrowLeft, Loader2, Download, Search, RefreshCw, Plus, FileUp, Filter, Building2 } from "lucide-react"
import { columns } from "./columns"
import { DataTable } from "@/components/data-table"
import ChartOfAccountsService from '@/api/chartofaccounts.service'
import * as XLSX from 'xlsx'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { BarChart3, PieChart } from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

function typeColor(type: string) {
  switch (type) {
    case 'ASSET': return 'bg-emerald-100 text-emerald-800';
    case 'LIABILITY': return 'bg-red-100 text-red-800';
    case 'EQUITY': return 'bg-indigo-100 text-indigo-800';
    case 'INCOME': return 'bg-blue-100 text-blue-800';
    case 'EXPENSE': return 'bg-amber-100 text-amber-800';
    default: return 'bg-slate-100 text-slate-800';
  }
}

// Define the necessary types directly in this file to avoid import issues
type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE'
interface ChartOfAccount {
  id?: number
  publicId: string
  code: string
  name: string
  type: AccountType
  isActive: boolean
  [key: string]: any // Allow other properties
}

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<ChartOfAccount[]> {
  try {
    const response = await ChartOfAccountsService.getAllChartOfAccounts(businessId, skip);
    console.log("getAllChartOfAccounts response.data", response.data);
    if (response.data.result === 'invalid') {
      window.location.href = '/logout';
      return [];
    }
    // Notify if exactly 50 accounts are returned
    if (response.data.chartOfAccounts.length === 50) {
      setCanLoadMore(true);
    }
    return response.data.chartOfAccounts;
  } catch (error) {
    console.error("Error fetching chart of accounts:", error);
    return [];
  }
}

export default function ChartOfAccounts() {
  const [data, setData] = useState<ChartOfAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [filterType, setFilterType] = useState<string>("");

  // Account type statistics
  const [accountStats, setAccountStats] = useState<{
    [key in 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE']: { count: number; active: number; inactive: number }
  }>({
    ASSET: { count: 0, active: 0, inactive: 0 },
    LIABILITY: { count: 0, active: 0, inactive: 0 },
    EQUITY: { count: 0, active: 0, inactive: 0 },
    INCOME: { count: 0, active: 0, inactive: 0 },
    EXPENSE: { count: 0, active: 0, inactive: 0 }
  });

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness?.id;
      if (!businessId) return;
      
      const result = await getData(setCanLoadMore, skip, businessId);
      console.log("result", result)
      setIsLoading(false);
      
      // Apply type filter if selected
      const filteredResults = filterType 
        ? result.filter(account => account.type === filterType)
        : result;
      
      setData(filteredResults);
      
      // Calculate statistics
      const stats = {
        ASSET: { count: 0, active: 0, inactive: 0 },
        LIABILITY: { count: 0, active: 0, inactive: 0 },
        EQUITY: { count: 0, active: 0, inactive: 0 },
        INCOME: { count: 0, active: 0, inactive: 0 },
        EXPENSE: { count: 0, active: 0, inactive: 0 }
      };
      
      result.forEach((account: ChartOfAccount) => {
        const accountType = account.type as 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
        if (stats[accountType]) {
          stats[accountType].count++;
          if (account.isActive) {
            stats[accountType].active++;
          } else {
            stats[accountType].inactive++;
          }
        }
      });
      
      setAccountStats(stats);
    };
    
    fetchData();
  }, [skip, filterType, selectedBusiness?.id]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chart of Accounts");
    const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `Chart_of_Accounts_${currentDateTime}.xlsx`);
  };

  const handleFilterChange = (value: string) => {
    setFilterType(value);
    // Reset pagination when filter changes
    setSkip(0);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => window.history.back()} 
            variant="ghost" 
            size="icon" 
            className="rounded-full h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold">Business Chart of Accounts</h1>
          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 hover:bg-blue-50">
            {data.length} accounts
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToExcel} 
            variant="outline" 
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Download className="h-4 w-4" /> Export
          </Button>
          
          <Button 
            onClick={() => window.location.href = "/chart-of-accounts/all"} 
            variant="outline" 
            className="flex items-center gap-2 text-sm font-medium bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
          >
            <Building2 className="h-4 w-4" /> See All Accounts
          </Button>
          
          <Button 
            onClick={() => window.location.href = "/chart-of-accounts/create"} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" /> Create Account
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="list" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="list" className="flex items-center gap-1">
            <Filter className="h-4 w-4" /> List View
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-1">
            <PieChart className="h-4 w-4" /> Statistics
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="list" className="space-y-4">
          <Card className="shadow-sm border-0">
            <CardHeader className="pb-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search accounts..."
                    className="pl-9 h-10 w-full md:w-[300px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-blue-500"
                  />
                </div>
                
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                  <Select value={filterType} onValueChange={handleFilterChange}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="ASSET">Assets</SelectItem>
                      <SelectItem value="LIABILITY">Liabilities</SelectItem>
                      <SelectItem value="EQUITY">Equity</SelectItem>
                      <SelectItem value="INCOME">Income</SelectItem>
                      <SelectItem value="EXPENSE">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            
            <Separator />
            
            <CardContent className="p-0">
              <DataTable columns={columns} data={data} section="chartofaccounts" isLoading={isLoading} />
              
              {canLoadMore && (
                <div className="flex flex-col items-center py-6">
                  <Button 
                    onClick={() => setSkip(skip + 50)} 
                    variant="outline" 
                    className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Load More
                  </Button>
                  <p className="text-sm text-slate-500 mt-2">
                    Showing {skip + 1} to {skip + Math.min(data.length, 50)} of {skip + data.length}+ accounts
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="stats" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {Object.entries(accountStats).map(([type, stats]) => (
              <Card key={type} className={`overflow-hidden border-t-4 ${typeColor(type)}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">{type}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.count}</div>
                  <div className="text-sm text-slate-500 mt-1">
                    <span className="text-green-600">{stats.active} active</span>
                    {stats.inactive > 0 && (
                      <span className="text-slate-400 ml-2">{stats.inactive} inactive</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Account Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] flex items-center justify-center">
                <div className="flex flex-col items-center justify-center">
                  <BarChart3 className="h-16 w-16 text-slate-300" />
                  <p className="text-slate-500 mt-2">Charts will be displayed here</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 